// src/pages/NewsManagement.jsx
import React, { useState, useEffect } from 'react';
import { getAllNews, updateNews, deleteNews } from '../../../services/adminService'; // Make sure path is correct
// No need for NewsManagementPage.css anymore if using Bootstrap entirely

const NewsManagement = () => { // Component name changed to NewsManagement
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending_approval'); // Default filter to pending approval

  // State for pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch news based on current filter status and pagination
      const params = { page, limit };
      if (filterStatus && filterStatus !== 'all') { // 'all' means no status filter
        params.status = filterStatus;
      }
      const data = await getAllNews(params); // Assuming data.data holds the array of news items
      setNewsList(data.data);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to fetch news.');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [filterStatus, page, limit]); // Re-fetch when filter status, page, or limit changes

  const handleChangeStatus = async (newsId, newStatus) => {
    if (window.confirm(`Are you sure you want to change status to "${newStatus}" for news ID: ${newsId}?`)) {
      setLoading(true); // Show loading while updating
      try {
        await updateNews(newsId, { status: newStatus });
        alert(`News ID: ${newsId} status updated to ${newStatus} successfully!`);
        fetchNews(); // Re-fetch news list to show updated status
      } catch (err) {
        // Use err.response?.data?.message for backend error messages
        setError(err.response?.data?.message || err.message || `Failed to update status for news ID: ${newsId}.`);
        console.error('Error updating news status:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (window.confirm(`Are you sure you want to delete news ID: ${newsId}? This action cannot be undone.`)) {
      setLoading(true);
      try {
        await deleteNews(newsId);
        alert(`News ID: ${newsId} deleted successfully!`);
        fetchNews(); // Re-fetch news list
      } catch (err) {
        setError(err.response?.data?.message || err.message || `Failed to delete news ID: ${newsId}.`);
        console.error('Error deleting news:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-warning text-dark';
      case 'draft': return 'bg-secondary';
      case 'posted': return 'bg-success';
      case 'live': return 'bg-primary';
      case 'rejected': return 'bg-danger';
      default: return 'bg-info';
    }
  };

  return (
    <div className="container mt-5">
      <h1 className="mb-4 text-center">News Management (Admin Panel)</h1>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="alert alert-info">Loading news...</div>}

      <div className="mb-3 d-flex align-items-center">
        <label htmlFor="statusFilter" className="form-label me-2 mb-0">Filter by Status:</label>
        <select
          id="statusFilter"
          className="form-select w-auto"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1); // Reset page when filter changes
          }}
          disabled={loading}
        >
          <option value="all">All News</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="draft">Draft</option>
          <option value="posted">Posted</option>
          <option value="live">Live</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {newsList.length === 0 && !loading && (
        <p className="text-center text-muted">No news found for the selected filter.</p>
      )}

      {!loading && newsList.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead>
              <tr>
              
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Reporter</th>
                <th>Created At</th>
                <th>Published At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {newsList.map((news) => (
                <tr key={news._id}>
             
                  <td>{news.title}</td>
                  <td>{news.category}</td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(news.status)}`}>
                      {news.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {news.reporterDetails ? (
                      <>
                        {news.reporterDetails.name} ({news.reporterDetails.role})
                        <br />
                        <small>
                          {news.reporterDetails.city ? news.reporterDetails.city + ', ' : ''}
                          {news.reporterDetails.state ? news.reporterDetails.state : ''}
                        </small>
                      </>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{news.createdAtDate} <br />{news.createdAtTime}</td>
                  <td>{news.postedDate || 'N/A'} <br />{news.postedTime || ''}</td>
                  <td>
                    <div className="d-flex flex-column gap-2">
                      {/* Approve/Reject buttons for pending news */}
                      {news.status === 'pending_approval' && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleChangeStatus(news._id, 'posted')}
                            disabled={loading}
                          >
                            Approve (Post)
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleChangeStatus(news._id, 'rejected')}
                            disabled={loading}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {/* General Update (e.g., set to draft from live/posted) / Edit button */}
                      {(news.status === 'posted' || news.status === 'live') && ( // Only Admin can change status for posted/live
                        <button
                          className="btn btn-sm btn-info text-white"
                          onClick={() => handleChangeStatus(news._id, 'draft')}
                          disabled={loading}
                        >
                          Set to Draft
                        </button>
                      )}
                      {/* Delete button (Admin can delete any status) */}
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleDeleteNews(news._id)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <button
            className="btn btn-primary"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <span className="fw-bold">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-primary"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={page === totalPages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;
